/**
 * 📌 Script: cores.js
 * 📂 Caminho: frontend/src/services/cores.js
 * 📝 Descrição: Acesso em JavaScript à paleta de cores do tailwind.config.js,
 *   para os casos em que uma classe Tailwind não basta: PDF (jsPDF), gráficos
 *   (Chart.js), canvas, estilos de janela de impressão e `<input type="color">`.
 *
 * 🔄 Relação com o Projeto:
 * - A paleta chega pelo build: vue.config.js serializa `theme.colors` em
 *   `process.env.VUE_APP_CORES`; vitest.config.js faz o mesmo nos testes.
 * - Nenhum valor de cor é repetido aqui. Alterar tailwind.config.js basta.
 *
 * 📥 Entradas (Parâmetros):
 * - `nome` (String) - Nome do token, igual ao da classe (ex.: "azul-profundo").
 * - `alpha` (Number) - Opacidade de 0 a 1, em `corRgba`.
 *
 * 📤 Saídas:
 * - Hex (`cor`), vetor [r, g, b] (`corRgb`, formato do jsPDF) ou string
 *   `rgba(r, g, b, a)` (`corRgba`, formato do Chart.js).
 *
 * 🧩 Exports:
 * - `cores` - Objeto congelado com a paleta completa.
 * - `cor(nome)` - Valor do token; lança erro se o token não existir.
 * - `corRgb(nome)` - Componentes RGB do token.
 * - `corRgba(nome, alpha)` - Cor com opacidade.
 *
 * 🔗 Dependências:
 * - tailwind.config.js (via variável de build `VUE_APP_CORES`).
 */

export const cores = Object.freeze(
  JSON.parse(process.env.VUE_APP_CORES || "{}")
);

export function cor(nome) {
  const valor = cores[nome];
  if (!valor) {
    throw new Error(`Token de cor inexistente em tailwind.config.js: ${nome}`);
  }
  return valor;
}

export function corRgb(nome) {
  const hex = cor(nome);
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) {
    throw new Error(`Token de cor sem valor hexadecimal: ${nome}`);
  }
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}

export function corRgba(nome, alpha = 1) {
  const [r, g, b] = corRgb(nome);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
